
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import { options } from '@univdiam/ui/lib/StatusOptions';
import { cn } from '@univdiam/ui/lib/utils';
import type { ActionType, NotificationType } from '@univdiam/ui/type'
import { format } from 'date-fns';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';


type TemplateInput = {
    name?: string;
    metadata: Record<string, string>;
};

const activityTemplates: Record<ActionType, (input: TemplateInput) => JSX.Element> = {
    order_created: ({ name = "Someone", metadata }) => (
        <p className='body2 '>
            {name} created an order <span className="font-semibold ">“{metadata?.job_title || "Untitled"}”</span>
        </p>
    ),

    status_updated: ({ name = "Someone", metadata }) => (
        <p className='body2'>
            {name} changed the status to <span className="font-semibold">“{options.find(o => o.id === metadata?.status)?.label || "Unknown"}”</span>
        </p>
    ),
    chat_message: ({ name = "Someone" }) => (
        <p className='body2'>
            {name} sent a new message
        </p>
    ),
    additional_instruction_added: ({ name = "Someone", metadata }) => (
        <p className="body2">
            {name} added a <span className="font-semibold">Special Instruction</span> under label <span className="font-semibold">“{metadata?.newLabel || "Unknown"}”</span>
        </p>
    ),
    additional_instruction_deleted: ({ name = "Someone", metadata }) => (
        <p className="body2">
            {name} removed a <span className="font-semibold">Special Instruction</span> under label <span className="font-semibold">“{metadata?.label || "Unknown"}”</span>
        </p>
    ),
    additional_instruction_updated: ({ name = "Someone", metadata }) => (
        <p className="body2">
            {name} updated a <span className="font-semibold">Special Instruction</span> under label {
                metadata?.previousLable !== metadata?.newLabel ? (
                    <>
                        <span className="font-semibold">“{metadata?.previousLable || "Unknown"}”</span> to{" "}
                        <span className="font-semibold">“{metadata?.newLabel || "Unknown"}”</span>
                    </>
                ) : (
                    <span className="font-semibold">“{metadata?.newLabel || "Unknown"}”</span>
                )
            }
        </p>
    ),
    cad_added: ({ name = "Someone" }) => (
        <p className="body2">
            {name} added a <span className="font-semibold">CAD</span>
        </p>
    ),
    cad_order_created: ({ name = "Someone", metadata }) => (
        <p className="body2">
            {name} created a CAD order <span className="font-semibold">“{metadata?.job_title || "Untitled"}”</span>
        </p>
    )
};



function formatNotification(n: NotificationType) {
    const { action_type, metadata, action_by_name } = n;

    const name = action_by_name || 'Someone';

    const templateFn = activityTemplates[action_type as ActionType];

    return templateFn ? templateFn({ name, metadata }) : `${name} performed an unknown action`;
}

function notificationTarget(data: NotificationType, isAdmin?: boolean) {
    const metaOrderId = data.metadata?.order_id ? String(data.metadata.order_id) : '';
    const metaOrderRef = data.metadata?.order_ref ? String(data.metadata.order_ref) : '';

    // Sometimes metadata flips the naming (e.g. order_id='UD1058', order_ref='58')
    const isMetaFlipped = metaOrderId.startsWith('UD') || metaOrderRef.match(/^\d+$/);

    const pk = (isMetaFlipped && metaOrderRef) ? metaOrderRef : (data.metadata?.order_id || data.notification_order_ref);
    const displayId = (isMetaFlipped && metaOrderId) ? metaOrderId : (data.metadata?.order_ref || data.actual_order_id);

    const customerId = data.metadata?.customer_id || data.order_customer_id;

    if (!pk) return null;

    return isAdmin === true ? `/order/${pk}/${displayId}/${customerId}` : `/order/${displayId}/${customerId}`;
}

const NotifCard = ({ data, isAdmin, onMarkAsSeen, isLast }: { isAdmin?: boolean, data: NotificationType, onMarkAsSeen?: (id: string | number) => void, isLast?: boolean }) => {
    const target = notificationTarget(data, isAdmin)
    const markSeen = () => {
        if (!data.is_seen && onMarkAsSeen) onMarkAsSeen(data.id!)
    }

    const content = (
        <>
            <div>
                <div className={cn('title1 mb-1 flex items-center gap-2 flex-wrap', data.is_seen ? "!font-semibold" : "!font-bold")}>
                    <span>{data.title}</span>
                    {!data.is_seen && <span className='bg-black rounded-lg text-xs md:text-sm font-semibold px-2 py-0.5 md:py-1 text-white'>New</span>}
                </div>
                <span className="text-xs text-[#883734] font-medium mb-1 block">
                    Order: {data.actual_order_id || data.notification_order_ref || `#${data.order_id}`}
                </span>
                {formatNotification(data)}
            </div>
            <p className='mt-2 text-sm'>{format(new Date(data.notification_created_at!), 'dd MMM yyyy')} &#x2022;  {formatTimeInUserTimezone(data.notification_created_at!)}</p>
        </>
    )

    return (
        <div className={cn('flex items-start gap-2 transition-colors', !data.is_seen && 'bg-amber-50', !isLast && 'border-b border-gray-100')}>
            {/* Real link so Cmd/Ctrl/middle-click open the order in a new tab */}
            {target ? (
                <Link
                    to={target}
                    onClick={markSeen}
                    onAuxClick={(e) => { if (e.button === 1) markSeen() }}
                    className='block min-w-0 flex-1 p-4'
                >
                    {content}
                </Link>
            ) : (
                <div className='min-w-0 flex-1 p-4' onClick={markSeen}>{content}</div>
            )}
            {!data.is_seen && onMarkAsSeen && (
                <button
                    type="button"
                    onClick={markSeen}
                    className='m-4 ms-0 shrink-0 cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-[#883734] hover:bg-[#883734]/10'
                >
                    Mark as read
                </button>
            )}
        </div>
    )
}

export default NotifCard